'use client'

import { Button, Card, Select, Text, Title } from '@mantine/core'
import { RiCheckboxCircleFill } from '@remixicon/react'
import { successToast, useCountryState } from '@repo/ui'
import { useMutation } from '@tanstack/react-query'
import { useRouter } from 'next/navigation'
import { useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { uploadKycDocument } from '../../../../api/kyc'
import { CustomFileInput } from './CustomFileInput'
import styles from './Manual.module.css'

const DOCUMENT_TYPES = [
  { label: 'Driving License', value: 'DRIVING_LICENSE' },
  { label: 'Selfie with ID', value: 'SELFIE_WITH_ID' },
  { label: 'ID Proof (Front)', value: 'ID_PROOF_FRONT' },
]

type FormValues = {
  idType: string
  idDocument: File | null
  idProofBack: File | null
  proofOfAddress: File | null
  passportPhoto: File | null
  country_context: string
}

type ManualVerificationFormProps = {
  onComplete: () => void
}
export default function ManualVerificationForm({
  onComplete,
}: ManualVerificationFormProps) {
  const [step, setStep] = useState<'form' | 'checklist'>('form')
  const [formData, setFormData] = useState<FormValues | null>(null)
  const router = useRouter()
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      idType: '',
      idDocument: null,
      idProofBack: null,
      proofOfAddress: null,
      passportPhoto: null,
      country_context: '',
    },
  })

  const selectedType = useWatch({ control, name: 'idType' })
  const selectedCountry = useWatch({ control, name: 'country_context' })

  const { mutateAsync: uploadDoc, isPending: isUploading } = useMutation({
    mutationFn: uploadKycDocument,
  })

  const validateFile = (file: File | null) => {
    if (!file) return 'This file is required'
    const validTypes = ['application/pdf', 'image/jpeg', 'image/png']
    return validTypes.includes(file.type) || 'Only PDF, JPG, or PNG allowed'
  }

  // Save data and move to checklist
  const onSaveAndContinue = (data: FormValues) => {
    setFormData(data)
    setStep('checklist')
  }

  // Upload all docs
  const onSubmitForVerification = async () => {
    if (!formData) return

    try {
      if (formData.idDocument) {
        await uploadDoc({
          document_type: `${selectedType}`,
          country_context: `${selectedCountry}`,
          document_file: formData.idDocument,
        })
      }

      if (selectedType === 'ID_PROOF_FRONT' && formData.idProofBack) {
        await uploadDoc({
          document_type: 'ID_PROOF_BACK',
          country_context: `${selectedCountry}`,
          document_file: formData.idProofBack,
        })
      }

      if (formData.proofOfAddress) {
        await uploadDoc({
          document_type: 'ADDRESS_PROOF',
          country_context: `${selectedCountry}`,
          document_file: formData.proofOfAddress,
        })
      }
      if (formData.passportPhoto) {
        await uploadDoc({
          document_type: 'PASSPORT',
          country_context: `${selectedCountry}`,
          document_file: formData.passportPhoto,
        })
      }
      successToast('Data saved successfully!')
      router.push('/profile-details?tab=documents')
    } catch (err) {
      console.error('Upload failed', err)
    }
  }

  return (
    <div className='w-full'>
      {step === 'form' && (
        <div className={styles.manualForm}>
          <Title order={2} className={styles.formTitle} ta='center'>
            Select & Upload ID Type
          </Title>
          <Text className={styles.formSubtitle} ta='center'>
            Select the type of ID you'd like to upload.
          </Text>

          <form
            onSubmit={handleSubmit(onSaveAndContinue)}
            className={styles.formBody}
          >
            {/* ID Type */}
            <Controller
              name='idType'
              control={control}
              rules={{ required: 'ID type is required' }}
              render={({ field }) => (
                <Select
                  label='ID Document Type*'
                  placeholder='Select ID type'
                  data={DOCUMENT_TYPES}
                  {...field}
                  error={errors.idType?.message}
                  styles={sharedInputStyles}
                />
              )}
            />
            <Controller
              name='country_context'
              control={control}
              rules={{ required: 'Country is required' }}
              render={({ field }) => {
                const selectedCountry = field.value
                const { countries } = useCountryState(
                  selectedCountry || '',
                  setValue,
                )

                return (
                  <Select
                    label='Country*'
                    placeholder='Select country'
                    data={countries}
                    {...field}
                    error={errors.country_context?.message}
                    styles={sharedInputStyles}
                  />
                )
              }}
            />

            {/* ID Document */}
            <Controller
              name='idDocument'
              control={control}
              rules={{ validate: validateFile }}
              render={({ field }) => (
                <CustomFileInput
                  id='idDocument'
                  label='Upload ID document*'
                  accept='.pdf, .jpeg, .jpg, .png'
                  value={field.value || null}
                  error={errors.idDocument?.message as string}
                  onChange={field.onChange}
                />
              )}
            />
            {selectedType === 'ID_PROOF_FRONT' && (
              <Controller
                name='idProofBack'
                control={control}
                rules={{ validate: validateFile }}
                render={({ field }) => (
                  <CustomFileInput
                    id='idProofBack'
                    label='Upload ID Proof (Back)*'
                    accept='.pdf, .jpeg, .jpg, .png'
                    value={field.value || null}
                    error={errors.idProofBack?.message as string}
                    onChange={field.onChange}
                  />
                )}
              />
            )}
            {/* Proof of Address */}
            <Controller
              name='proofOfAddress'
              control={control}
              rules={{ validate: validateFile }}
              render={({ field }) => (
                <CustomFileInput
                  id='proofOfAddress'
                  label='Upload Proof of Address*'
                  accept='.pdf, .jpeg, .jpg, .png'
                  value={field.value || null}
                  error={errors.proofOfAddress?.message as string}
                  onChange={field.onChange}
                />
              )}
            />

            {/* Passport Photo */}
            <Controller
              name='passportPhoto'
              control={control}
              rules={{ validate: validateFile }}
              render={({ field }) => (
                <CustomFileInput
                  id='passportPhoto'
                  label='Upload Recent Passport Photo*'
                  accept='.pdf, .jpeg, .jpg, .png'
                  value={field.value || null}
                  error={errors.passportPhoto?.message as string}
                  onChange={field.onChange}
                />
              )}
            />

            <Button
              type='submit'
              fullWidth
              mt='md'
              styles={{
                root: {
                  padding: '6px 14px',
                  backgroundColor: 'var(--color-kycbtn-Choose)',
                  fontSize: '12px',
                  fontWeight: 500,
                  color: '#fff',
                  borderRadius: '8px',
                  '&:hover': {
                    backgroundColor: 'var(--color-kycbtn-Choose)',
                  },
                },
              }}
            >
              Save & Continue
            </Button>
          </form>
        </div>
      )}

      {step === 'checklist' && formData && (
        <div className={styles.manualChecklist}>
          <Title order={2} className={styles.formTitle} ta='center'>
            Manual Verification
          </Title>

          <Text className={styles.formSubtitle} ta='center'>
            Please ensure all documents are clear and valid. Verification may
            take up to 2–3 business days.
          </Text>

          {/* Card */}
          <Card
            shadow='sm'
            radius='lg'
            padding='lg'
            withBorder
            className='mb-6'
          >
            <Text className={styles.checklistCardTitle}>
              Document Checklist
            </Text>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[
                'ID Document uploaded',
                'Proof of Address uploaded',
                'Passport Photo uploaded',
              ].map((item) => (
                <div key={item} className={styles.checklistItem}>
                  <div className={styles.checklistIconWrapper}>
                    <RiCheckboxCircleFill size={16} />
                  </div>
                  <Text className={styles.checklistItemText}>{item}</Text>
                </div>
              ))}
            </div>
          </Card>

          {/* Submit Button */}
          <Button
            fullWidth
            color='green'
            onClick={onSubmitForVerification}
            loading={isUploading}
            styles={{
              root: {
                padding: '6px 14px',
                backgroundColor: 'var(--color-kycbtn-Choose)',
                fontSize: '12px',
                fontWeight: 500,
                color: '#fff',
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: 'var(--color-kycbtn-Choose)',
                },
              },
            }}
          >
            Submit for Verification
          </Button>
        </div>
      )}
    </div>
  )
}

const sharedInputStyles = {
  input: {
    backgroundColor: '#FFFFFF99',
    borderRadius: 8,
    color: '#9B9EA4',
    fontSize: 14,
    fontWeight: 500,
    paddingLeft: 18,
    paddingRight: 18,
  },
  label: {
    color: '#344054',
    fontSize: 14,
    fontWeight: 500,
    marginBottom: 6,
  },
}
